
import { SVGMotionProps, Variants, motion } from 'framer-motion';
import { SVGProps } from 'react';

import { cn } from '@/lib/utils';

export const FlagSyncLoader = ({
  className,
  ...rest
}: SVGMotionProps<SVGSVGElement>) => {
  const paths = [
    'M0 62.0294C-8.05927e-07 55.4182 5.4351 50.0588 12.1396 50.0588C18.8442 50.0588 24.2793 55.4182 24.2793 62.0294C24.2793 68.6406 18.8442 74 12.1396 74C5.4351 74 8.05927e-07 68.6406 0 62.0294Z',
    'M4.90842e-06 52.4554C3.82047 44.8079 9.50984 37.6422 15.8025 36.0941C23.8694 34.1095 29.2645 36.0491 34.9464 38.0918C39.2569 39.6414 43.7325 41.2504 49.665 41.2504C55.8699 41.2504 64.8837 34.6452 72.5531 24.51H4.90842e-06V52.4554Z',
    'M84.6141 1.27632C84.8132 0.640089 84.3333 0 83.6666 0H1C0.44772 0 4.90842e-06 0.447714 4.90842e-06 0.999999V17.6883H77.1672C80.1858 12.6812 82.7829 7.1255 84.6141 1.27632Z',
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0,
        when: 'beforeChildren',
        staggerChildren: 0.2,
      },
    },
  };

  const pathVariants: Variants = {
    hidden: { opacity: 0, scale: 0.25 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        type: 'spring',
        stiffness: 100,
        repeat: Infinity,
        repeatType: 'loop',
        repeatDelay: 0.25,
      },
    },
  };

  return (
    <motion.svg
      viewBox="0 0 85 74"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn('aspect-85/74', className)}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      {...rest}
    >
      {paths.map((d, index) => (
        <motion.path key={index} d={d} fill="#1CEFA5" variants={pathVariants} />
      ))}
    </motion.svg>
  );
};

export const FlagSyncLogo = ({
  className,
  ...rest
}: SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 85 74"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={cn('aspect-85/74', className)}
    {...rest}
  >
    <path
      d="M0 62.0294C-8.05927e-07 55.4182 5.4351 50.0588 12.1396 50.0588C18.8442 50.0588 24.2793 55.4182 24.2793 62.0294C24.2793 68.6406 18.8442 74 12.1396 74C5.4351 74 8.05927e-07 68.6406 0 62.0294Z"
      fill="#1CEFA5"
    />
    <path
      d="M4.90842e-06 52.4554C3.82047 44.8079 9.50984 37.6422 15.8025 36.0941C23.8694 34.1095 29.2645 36.0491 34.9464 38.0918C39.2569 39.6414 43.7325 41.2504 49.665 41.2504C55.8699 41.2504 64.8837 34.6452 72.5531 24.51H4.90842e-06V52.4554Z"
      fill="#1CEFA5"
    />
    <path
      d="M84.6141 1.27632C84.8132 0.640089 84.3333 0 83.6666 0H1C0.44772 0 4.90842e-06 0.447714 4.90842e-06 0.999999V17.6883H77.1672C80.1858 12.6812 82.7829 7.1255 84.6141 1.27632Z"
      fill="#1CEFA5"
    />
  </svg>
);

export const FlagSyncLogoWithTitle = ({
  dark = false,
  className,
  ...rest
}: SVGProps<SVGSVGElement> & { dark?: boolean }) => {
  return (
    <svg
      viewBox="0 0 170 36"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...rest}
    >
      <path
        d="M163.558 28.27C159.838 28.27 156.328 25.63 156.328 20.5C156.328 15.61 159.418 12.82 163.678 12.82C165.928 12.82 167.848 13.66 169.138 14.23L169.168 18.25H169.048C167.998 16.51 166.048 13.9 163.768 13.9C161.308 13.9 160.168 16.57 160.168 19.57C160.198 23.29 161.848 25.87 165.118 25.87C166.438 25.87 168.058 25.45 169.768 24.01L169.858 24.07C168.928 26.32 166.678 28.27 163.558 28.27Z"
        fill={dark ? '#1F2231' : 'white'}
      />
      <path
        d="M145.479 27.88V28H140.589V27.88C140.709 27.34 141.279 25.39 141.279 23.53V17.47C141.279 15.58 140.709 13.66 140.589 13.09V13H144.789V15.58C145.959 13.9 147.549 12.82 149.649 12.82C152.199 12.82 154.569 14.32 154.569 17.89V23.53C154.569 25.39 155.139 27.34 155.259 27.88V28H150.339V27.88C150.459 27.34 151.029 25.39 151.029 23.53V18.34C151.029 16.03 149.619 15.1 148.089 15.07C146.919 15.07 145.839 15.58 144.789 16.48V23.53C144.789 25.39 145.359 27.34 145.479 27.88Z"
        fill={dark ? '#1F2231' : 'white'}
      />
      <path
        d="M125.339 33.37L126.059 29.95L126.119 29.92C127.919 31.69 130.319 32.59 131.399 29.92L131.969 28.48L126.869 17.05C126.089 15.34 125.069 13.66 124.529 13.09V13H130.589V13.09C130.349 13.63 129.929 15.31 130.649 16.9L133.739 24.01L136.589 16.96C137.249 15.34 136.769 13.63 136.559 13.09V13H139.859V13.09C139.619 13.45 138.689 14.98 138.059 16.48L132.389 30.55C130.979 34.06 128.609 34.66 125.339 33.37Z"
        fill={dark ? '#1F2231' : 'white'}
      />
      <path
        d="M116.869 28.33C114.409 28.33 111.739 27.52 109.039 26.29V21.85H109.219C111.289 24.16 113.959 27.07 117.169 27.07C119.419 27.07 121.189 25.75 121.189 23.53C121.189 20.95 118.729 19.69 115.369 18.55C111.349 17.2 108.829 15.22 108.829 12.19C108.829 9.04004 111.559 6.10004 116.539 6.10004C119.359 6.10004 122.029 7.12004 123.799 7.93004V12.16H123.679C121.699 10.03 118.849 7.33004 116.029 7.33004C113.719 7.33004 112.189 8.80004 112.189 10.48C112.189 12.7 114.889 13.96 118.399 15.22C122.989 16.96 124.609 19.33 124.609 22.06C124.609 26.32 120.949 28.33 116.869 28.33Z"
        fill={dark ? '#1F2231' : 'white'}
      />
      <path
        d="M99.5828 23.4401C98.7128 23.4401 97.8128 23.2601 96.9728 22.9601C96.2528 23.2001 95.6828 23.6501 95.6828 24.2501C95.6828 25.1801 96.7628 25.3001 97.8728 25.3001H102.763C105.583 25.3001 107.353 26.8001 107.353 29.4101C107.353 33.3101 102.883 34.0001 99.8528 34.0001C97.5728 34.0001 93.2228 33.4901 93.2228 30.4901C93.2228 29.5301 93.7028 28.6301 94.4828 27.9701C93.7028 27.3701 93.4328 26.5301 93.4328 25.6901C93.4328 24.0101 94.4828 22.9901 96.0728 22.5401C94.5128 21.6701 93.3428 20.2001 93.3428 18.1001C93.3428 14.6201 96.6128 12.8201 99.5828 12.8201C100.753 12.8201 101.953 13.0901 103.003 13.6301C103.843 11.9801 105.343 10.9901 107.503 11.2901L106.963 14.1401L106.873 14.1701C105.943 13.6601 104.803 13.4801 103.573 13.9601C104.923 14.8301 105.913 16.2101 105.913 18.1001C105.913 21.5801 102.553 23.4101 99.5828 23.4401ZM99.5828 22.3601C101.323 22.3601 102.373 20.5301 102.373 18.1001C102.373 15.6701 101.323 13.8701 99.5828 13.8701C97.8728 13.8701 96.8528 15.6701 96.8528 18.1001C96.8528 20.5301 97.8728 22.3601 99.5828 22.3601ZM95.3828 29.3801C95.3528 31.3601 96.8528 32.8601 100.213 32.8601C102.883 32.8601 104.953 31.8701 104.953 30.4301C104.953 29.5001 104.293 28.7501 102.673 28.7501H97.6028C96.7328 28.7501 96.0128 28.6301 95.4728 28.4501C95.3828 28.7201 95.3828 29.0501 95.3828 29.3801Z"
        fill={dark ? '#1F2231' : 'white'}
      />
      <path
        d="M82.7159 28.24C79.5959 28.24 77.8859 25.96 77.8859 23.65C77.8859 20.35 81.3959 18.4 85.4159 18.94C86.0759 19.03 86.7059 19.15 87.1859 19.3V17.44C87.1859 15.19 86.3159 13.99 85.0259 13.99C83.1959 13.99 79.6559 16.87 79.0559 17.71H78.9359V13.99C81.2759 13.21 83.6159 12.82 85.2359 12.82C88.8659 12.82 90.7259 14.71 90.7259 17.59V24.49C90.7259 26.08 91.3559 26.68 92.6459 26.17L92.6759 26.26C92.3759 27.37 91.4159 28.15 90.0659 28.15C88.5659 28.15 87.5759 27.34 87.2759 25.93C86.4359 27.1 84.9359 28.24 82.7159 28.24ZM81.6059 23.23C81.6059 25 82.8659 26.29 84.5459 26.29C85.5359 26.29 86.4359 25.81 87.1859 24.94V20.2C87.0359 20.2 86.8859 20.17 86.7359 20.14C83.3759 19.6 81.6059 21.22 81.6059 23.23Z"
        fill={dark ? '#1F2231' : 'white'}
      />
      <path
        d="M76.9956 27.88V28H72.0756V27.88C72.1956 27.34 72.7656 25.39 72.7656 23.53V10.57C72.7656 8.71003 72.1956 6.85002 71.9856 6.31002L71.9556 6.22003L72.6456 6.10003L76.3056 5.65002V23.53C76.3056 25.39 76.8756 27.34 76.9956 27.88Z"
        fill={dark ? '#1F2231' : 'white'}
      />
      <path
        d="M65.5658 7.54002H61.0058V16.33H63.0158C65.2958 16.33 67.6958 15.22 68.3258 15.04H68.4458V18.64H68.3258C67.6958 18.46 65.2958 17.38 63.0158 17.38H61.0058V28H56.5658V27.88C56.6858 27.34 57.2558 25.39 57.2558 23.53V10.87C57.2558 8.98003 56.6858 7.06002 56.5658 6.49003V6.40002H71.1758V10.54H71.0258C70.3658 10.18 67.9058 7.54002 65.5658 7.54002Z"
        fill={dark ? '#1F2231' : 'white'}
      />
      <path
        d="M0 30.1765C-3.98223e-07 26.9602 2.68558 24.3529 5.99841 24.3529C9.31124 24.3529 11.9968 26.9602 11.9968 30.1765C11.9968 33.3927 9.31124 36 5.99841 36C2.68558 36 3.98223e-07 33.3927 0 30.1765Z"
        fill="#1CEFA5"
      />
      <path
        d="M2.42534e-06 25.5188C1.88776 21.7984 4.69898 18.3124 7.8083 17.5593C11.7943 16.5938 14.4601 17.5374 17.2676 18.5311C19.3975 19.285 21.609 20.0678 24.5403 20.0678C27.6063 20.0678 32.0602 16.8544 35.8498 11.9238H2.42534e-06V25.5188Z"
        fill="#1CEFA5"
      />
      <path
        d="M41.5935 1.27601C41.8092 0.643313 41.3292 0 40.6607 0H1C0.447718 0 2.42534e-06 0.447715 2.42534e-06 1V8.60514H38.1297C39.5063 6.3569 40.7055 3.88134 41.5935 1.27601Z"
        fill="#1CEFA5"
      />
    </svg>
  );
};
